import { useTheme, Button } from "@mui/material";
import { t } from "i18next";
import FormatUtil from "../../Utility/ProjectComUtil";
import CoordinateSystem from "../CoordinateSystem";
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import RenameProjectDialog from "../../RenameProjectDialog";
import { IEnabledUpdateProjectDetail } from "../../../pages/ProjectDetail";
import { Common } from "../../../../common/src";
import CoordinateContext from "../Contexts/CoordinateContext";
import axios from "axios";
import { config } from "../../../config/config";
import { LoadingButton } from "@mui/lab";

interface ProjectDetailProps {
  isLanguage: boolean;
  projectDetail: Common.ProjectDetail;
  setProjectName?: Dispatch<SetStateAction<string>>;
  updateProjectDetail?: (newDetail: IEnabledUpdateProjectDetail) => void;
  showDialog: (show: boolean) => void;
}

export const ProjectDetailTable = (props: ProjectDetailProps) => {
  const { fetchCS, clearDashboardCS, fetchDashboardCS } =
    useContext(CoordinateContext);
  const [isShowRenamePopup, setIsShowRenamePopup] = useState(false);
  const [deviceName, setDeviceName] = useState(null);
  const [whileLoading, setWhileLoading] = useState(false);
  const theme = useTheme();

  const customColor = {
    color: theme.palette.base.contrast,
  };

  // style
  const styleKey: React.CSSProperties = {
    minHeight: "32px",
    minWidth: "150px",
    width: "150px",
    color: customColor.color,
    fontSize: "16px",
    marginLeft: "20px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  const styleValue: React.CSSProperties = {
    minHeight: "32px",
    width: "100%",
    maxWidth: "260px",
    color: customColor.color,
    fontSize: "16px",
    marginRight: "10px",
    overflowWrap: "break-word",
    wordWrap: "break-word",
  };

  const styleProjectDetailTableMainTitle: React.CSSProperties = {
    minHeight: "32px",
    fontSize: "16px",
  };

  const styleProjectDetailTableButton: React.CSSProperties = {
    fontSize: "16px",
  };

  const styleColon: React.CSSProperties = {
    minHeight: "32px",
    fontSize: "16px",
    marginRight: "10px",
  };

  const handleRename = () => {
    setIsShowRenamePopup(true);
  };

  const handleDelete = async () => {
    setWhileLoading(true);
    try {
      await axios
        .delete(
          `${config.serverHost}/api/v1/project/${props.projectDetail.id}/ar/ifc`,
          {
            withCredentials: true,
          }
        )
        .then(() => {
          props.projectDetail.ifc_file = null;
        });
    } catch (err) {
      console.error(err);
    } finally {
      setWhileLoading(false);
    }
  };

  useEffect(() => {
    props.showDialog(isShowRenamePopup);
  }, [isShowRenamePopup]);

  useEffect(() => {
    clearDashboardCS();
    setDeviceName(props.projectDetail?.metadata?.mobile_device_description);
    fetchCS(props.projectDetail?.id);
    fetchDashboardCS(props.projectDetail?.id);
  }, []);

  return (
    <>
      <div style={{ textAlign: "left" }}>
        <div style={styleProjectDetailTableMainTitle}>
          {"【" + t("POINT_CLOUD") + "】"}
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("PROJECT_NAME")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>{props.projectDetail?.name}</div>
          <div style={styleProjectDetailTableButton}>
            <Button
              size="small"
              variant="contained"
              onClick={() => handleRename()}
              // sx={{ borderRadius: "0px" }}
              style={{
                boxShadow: "none",
                borderRadius: "5px",
              }}
            >
              {t("RENAME")}
            </Button>
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("JOBSITE_NAME")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>{props.projectDetail?.jobsiteName}</div>
        </div>
        <CoordinateSystem
          styleKey={styleKey}
          styleColon={styleColon}
          styleValue={styleValue}
          buttonstyle={styleProjectDetailTableButton}
          jobsiteName={props.projectDetail?.jobsiteName}
          showDialog={props.showDialog}
        />
        <div style={{ display: "flex" }}>
          <div style={styleKey}>
            {t("NUMBER_OF_POINTS")}({t("BEFORE_REMOVE")})
          </div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>
            {props.projectDetail?.numberOfPoints &&
              props.projectDetail?.numberOfPoints.toLocaleString()}
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>
            {t("FILE_SIZE")}({t("BEFORE_REMOVE")})
          </div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>{props.projectDetail?.size}</div>
        </div>

        <div style={styleProjectDetailTableMainTitle}>
          {"【" + t("PHOTOS") + "】"}
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("PHOTOGRAPHER")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>{props.projectDetail?.photographer}</div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("DATE_AND_TIME_OF_PHOTO")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>
            {props.isLanguage
              ? FormatUtil.dateFormat1(props.projectDetail?.uploadTime, true)
              : FormatUtil.dateFormat1(props.projectDetail?.uploadTime, false)}
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("IMAGE_NUMBER")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>
            {props.projectDetail?.photoURLs.length + " " + t("PIECES")}
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("IMAGE_RESOLUTION")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>
            {props.projectDetail?.imageResolution
              ? `${props.projectDetail?.imageResolution} ${t("PIXELS")}`
              : "ー"}
          </div>
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("DEVICE")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>
            {deviceName !== null && deviceName !== "" ? deviceName : "ー"}
          </div>
        </div>
        {props.projectDetail.ifc_file && (
          <>
            <div style={styleProjectDetailTableMainTitle}>
              {"【" + t("DESIGN_DATA") + "】"}
            </div>
            <div style={{ display: "flex" }}>
              <div style={styleKey}>{t("FILE_NAME")}</div>
              <div style={styleColon}>:</div>
              <div style={styleValue}>{props.projectDetail.ifc_file}</div>
              <div style={styleProjectDetailTableButton}>
                <LoadingButton
                  size="small"
                  variant="contained"
                  onClick={(e) => handleDelete()}
                  sx={{ borderRadius: "0px" }}
                  style={{
                    backgroundColor: props.projectDetail.ifc_file
                      ? "#f44336"
                      : "",
                    boxShadow: "none",
                    borderRadius: "5px",
                  }}
                  disabled={!props.projectDetail.ifc_file}
                  loading={whileLoading}
                >
                  {t("REMOVE")}
                </LoadingButton>
              </div>
            </div>
          </>
        )}
        <div style={styleProjectDetailTableMainTitle}>
          {"【" + t("SFM_PROCESSING") + "】"}
        </div>
        <div style={{ display: "flex" }}>
          <div style={styleKey}>{t("CONSUMPTION_PGP")}</div>
          <div style={styleColon}>:</div>
          <div style={styleValue}>
            {props.projectDetail?.pgpConsume
              ? `${props.projectDetail?.pgpConsume} ${t("PGP_UNIT")}`
              : "ー"}
          </div>
        </div>
      </div>
      {isShowRenamePopup && (
        <RenameProjectDialog
          projectID={props.projectDetail?.id}
          projectName={props.projectDetail?.name}
          setProjectName={props.setProjectName}
          updateProjectDetail={props.updateProjectDetail}
          openDialog={isShowRenamePopup}
          setOpenDialog={setIsShowRenamePopup}
        />
      )}
    </>
  );
};
